/**
 * Holidays Settings Component (Enhanced)
 * Verwaltet Feiertage und Ferien der Werkstatt mit Live-Validierung und verbesserter Fehlerbehandlung
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Event as EventIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Compare as CompareIcon,
  CloudSync as CloudSyncIcon,
  Warning as WarningIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import {
  Holiday,
  HolidayType,
  CreateHolidayRequest,
  ThreeCXStatus,
  ThreeCXSyncAnalysis,
  ThreeCXComparison,
} from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import ThreeCXIntegration from "./ThreeCXIntegration";

const HolidaysSettings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [conflictingHolidays, setConflictingHolidays] = useState<Holiday[]>([]);
  const [showConflictResolver, setShowConflictResolver] = useState(false);
  const [conflictResolution, setConflictResolution] = useState<{
    [key: string]: boolean;
  }>({});

  // 3CX Integration state
  const [threeCXStatus, setThreeCXStatus] = useState<ThreeCXStatus | null>(
    null
  );
  const [threeCXError, setThreeCXError] = useState<string | null>(null);
  const [threeCXLoading, setThreeCXLoading] = useState(false);
  const [comparison, setComparison] = useState<ThreeCXComparison | null>(null);

  const [formData, setFormData] = useState<CreateHolidayRequest>({
    name: "Betriebsferien",
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 Tage später
    type: HolidayType.VACATION,
    description: "",
  });

  // Hilfsfunktionen für Überlappungscheck
  const checkDateOverlap = useMemo(() => {
    return (startDate: Date, endDate: Date, excludeId?: string): Holiday[] => {
      return holidays.filter((holiday) => {
        // Überspringen wenn es das gleiche Holiday ist (beim Bearbeiten)
        if (excludeId && holiday.id === excludeId) return false;

        // Nur aktive Feiertage prüfen
        if (!holiday.isActive) return false;

        const holidayStart = new Date(holiday.startDate);
        const holidayEnd = new Date(holiday.endDate);

        // Normalisierte Datumsvergleiche (ohne Zeit)
        const start = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        );
        const end = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate()
        );
        const hStart = new Date(
          holidayStart.getFullYear(),
          holidayStart.getMonth(),
          holidayStart.getDate()
        );
        const hEnd = new Date(
          holidayEnd.getFullYear(),
          holidayEnd.getMonth(),
          holidayEnd.getDate()
        );

        // Überlappung prüfen: (start <= hEnd) && (end >= hStart)
        return start <= hEnd && end >= hStart;
      });
    };
  }, [holidays]);

  // Live-Validierung bei Formular-Änderungen
  useEffect(() => {
    if (!addDialogOpen) return;

    setValidating(true);
    const errors: string[] = [];

    // Basis-Validierung
    if (!formData.name.trim()) {
      errors.push("Name ist erforderlich");
    }

    if (formData.startDate > formData.endDate) {
      errors.push("Startdatum darf nicht nach dem Enddatum liegen");
    }

    // Überlappungscheck nur wenn Daten vollständig sind
    if (formData.name.trim() && formData.startDate <= formData.endDate) {
      const conflicts = checkDateOverlap(formData.startDate, formData.endDate);
      setConflictingHolidays(conflicts);

      if (conflicts.length > 0) {
        const conflictNames = conflicts.map((h) => `"${h.name}"`).join(", ");
        errors.push(`Überlappung mit vorhandenen Feiertagen: ${conflictNames}`);
        setShowConflictResolver(true);
        // Automatisch alle Konflikte als "zu deaktivieren" markieren
        const autoResolution = conflicts.reduce((acc, conflict) => {
          acc[conflict.id] = true;
          return acc;
        }, {} as { [key: string]: boolean });
        setConflictResolution(autoResolution);
      } else {
        setShowConflictResolver(false);
        setConflictResolution({});
      }
    } else {
      setConflictingHolidays([]);
      setShowConflictResolver(false);
      setConflictResolution({});
    }

    setValidationErrors(errors);
    setValidating(false);
  }, [formData, addDialogOpen, checkDateOverlap]);

  // Hilfsfunktion für optimistische UI Updates mit Debugging
  const updateHolidayInState = (
    holidayId: string,
    updates: Partial<Holiday>
  ) => {
    console.log("🔄 Updating holiday in state:", { holidayId, updates });

    setHolidays((prev) => {
      const updated = prev.map((h) => {
        if (h.id === holidayId) {
          const updatedHoliday = { ...h, ...updates };
          console.log("✅ Holiday updated:", {
            before: h,
            after: updatedHoliday,
          });
          return updatedHoliday;
        }
        return h;
      });

      console.log("📊 State update completed, total holidays:", updated.length);
      return updated;
    });
  };

  // Load holidays mit besserer Fehlerbehandlung und Debugging
  const loadHolidays = async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      console.log("📥 Loading holidays...", { silent });

      const fetchedHolidays = await apiClient.getHolidays();

      console.log("✅ Holidays loaded:", {
        count: fetchedHolidays.length,
        active: fetchedHolidays.filter((h) => h.isActive).length,
        inactive: fetchedHolidays.filter((h) => !h.isActive).length,
      });

      setHolidays(fetchedHolidays);
    } catch (err: any) {
      console.error("❌ Fehler beim Laden der Feiertage:", err);
      if (!silent) {
        setError(err.message || "Fehler beim Laden der Feiertage");
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadHolidays();
  }, []);

  useEffect(() => {
    load3CXStatus();
  }, []);

  const handleForceUpdateGermanHolidays = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log("🚀 Starte Force-Update deutsche Feiertage...");
      const result = await apiClient.forceUpdateGermanHolidays();

      console.log("✅ Force-Update Ergebnis:", result);

      if (result.success) {
        // Erfolgreiche Aktualisierung - bessere Benutzerrückmeldung
        if (result.newHolidays > 0 || result.existingHolidays > 0) {
          // Neues Alert mit strukturierten Informationen
          setError(null);
          alert(
            `Deutsche Feiertage erfolgreich aktualisiert!\n\n` +
              `📊 Zusammenfassung:\n` +
              `• ${result.totalFetched} Feiertage von API abgerufen\n` +
              `• ${result.newHolidays} neue Feiertage erstellt\n` +
              `• ${result.existingHolidays} bereits vorhanden\n\n` +
              `${result.summary}`
          );
        } else {
          alert("Alle deutschen Feiertage sind bereits aktuell!");
        }
      } else {
        // Fehler aufgetreten
        console.error("❌ Force-Update mit Fehlern:", result.errors);
        setError(`Feiertage-Update mit Fehlern: ${result.errors.join(", ")}`);
      }

      // Holidays neu laden
      await loadHolidays(true);
    } catch (err: any) {
      console.error("❌ Force-Update Fehler:", err);
      setError(
        err.message || "Fehler beim Force-Update der deutschen Feiertage"
      );
    } finally {
      setSaving(false);
    }
  };

  const get3CXStatusColor = (): "success" | "warning" | "error" => {
    if (!threeCXStatus) return "error";
    if (!threeCXStatus.connected) return "error";
    if (!threeCXStatus.tokenStatus.isValid) return "warning";
    return "success";
  };

  const get3CXStatusText = (): string => {
    if (!threeCXStatus) return "Unbekannt";
    if (!threeCXStatus.connected) return "Getrennt";
    if (!threeCXStatus.tokenStatus.isValid) return "Token ungültig";
    return "Verbunden";
  };

  // 3CX Integration functions mit besserer Fehlerbehandlung
  const load3CXStatus = async () => {
    try {
      const status = await apiClient.get3CXStatus();
      setThreeCXStatus(status);
      setThreeCXError(null);
    } catch (err: any) {
      console.error("❌ Fehler beim Laden des 3CX Status:", err);
      setThreeCXError(err.message || "Fehler beim Laden des 3CX Status");
    }
  };

  const handleSync3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);
      await apiClient.sync3CXHolidays();
      await load3CXStatus();
      await loadHolidays(true); // Silent reload
    } catch (err: any) {
      console.error("❌ 3CX Sync Fehler:", err);
      setThreeCXError(err.message || "Fehler bei der 3CX Synchronisation");
    } finally {
      setThreeCXLoading(false);
    }
  };

  const handleCompare3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);
      const result = await apiClient.compare3CXHolidays();
      setComparison(result);
    } catch (err: any) {
      console.error("❌ 3CX Compare Fehler:", err);
      setThreeCXError(err.message || "Fehler beim Vergleich mit 3CX");
    } finally {
      setThreeCXLoading(false);
    }
  };

  const handleForceUpdate3CX = async () => {
    try {
      setThreeCXLoading(true);
      setThreeCXError(null);
      await apiClient.forceUpdate3CXHolidays();
      await load3CXStatus();
      await loadHolidays(true); // Silent reload
    } catch (err: any) {
      console.error("❌ 3CX Force Update Fehler:", err);
      setThreeCXError(err.message || "Fehler beim Force Update von 3CX");
    } finally {
      setThreeCXLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Wenn Startdatum geändert wird, automatisch Enddatum auf 7 Tage später setzen
      if (field === "startDate" && value instanceof Date) {
        newData.endDate = new Date(value.getTime() + 7 * 24 * 60 * 60 * 1000);
      }

      return newData;
    });
  };

  const handleAddHoliday = async () => {
    // Prüfe ob es Konflikte gibt und diese gelöst werden müssen
    const conflicts = checkDateOverlap(formData.startDate, formData.endDate);

    if (conflicts.length > 0) {
      // Prüfe ob alle Konflikte zur Deaktivierung markiert sind
      const unresolvedConflicts = conflicts.filter(
        (h) => !conflictResolution[h.id]
      );

      if (unresolvedConflicts.length > 0) {
        setError(
          `Bitte markieren Sie die konfligierenden Feiertage zur Deaktivierung oder ändern Sie den Zeitraum.`
        );
        return;
      }

      // Deaktiviere alle markierten konfligierenden Feiertage im Hintergrund
      console.log("🔄 Deaktiviere konfligierende Feiertage...");
      const deactivationPromises = conflicts
        .filter((conflict) => conflictResolution[conflict.id])
        .map(async (conflict) => {
          try {
            console.log(`🔽 Deaktiviere Feiertag: ${conflict.name}`);
            await apiClient.deactivateHoliday(conflict.id);

            // Optimistic Update
            updateHolidayInState(conflict.id, { isActive: false });
            return { success: true, conflict };
          } catch (err: any) {
            console.error(
              `❌ Fehler beim Deaktivieren von ${conflict.name}:`,
              err
            );
            return { success: false, conflict, error: err.message };
          }
        });

      // Alle Deaktivierungen parallel ausführen
      const deactivationResults = await Promise.all(deactivationPromises);

      // Prüfe auf Fehler bei der Deaktivierung
      const failedDeactivations = deactivationResults.filter(
        (result) => !result.success
      );
      if (failedDeactivations.length > 0) {
        const failedNames = failedDeactivations
          .map((r) => r.conflict.name)
          .join(", ");
        setError(`Fehler beim Deaktivieren von: ${failedNames}`);
        return;
      }

      // Liste der erfolgreich deaktivierten Feiertag-IDs für finale Prüfung
      const deactivatedIds = deactivationResults
        .filter((result) => result.success)
        .map((result) => result.conflict.id);

      console.log("✅ Erfolgreich deaktivierte Feiertage:", deactivatedIds);

      // Finale Überlappungsprüfung (schließe bereits deaktivierte Feiertage aus)
      const finalConflicts = checkDateOverlap(
        formData.startDate,
        formData.endDate
      ).filter((conflict) => !deactivatedIds.includes(conflict.id));

      if (finalConflicts.length > 0) {
        console.error(
          "❌ Unerwartete Konflikte nach Deaktivierung:",
          finalConflicts,
          "Deaktivierte IDs:",
          deactivatedIds
        );
        setError(
          `Unerwartete Konflikte nach Deaktivierung: ${finalConflicts
            .map((c: Holiday) => c.name)
            .join(", ")}. Bitte versuchen Sie es erneut.`
        );
        return;
      }
    }

    // Validierung vor dem Senden (nach Konfliktauflösung)
    const remainingErrors = validationErrors.filter(
      (e) => !e.includes("Überlappung")
    );

    try {
      setSaving(true);
      setError(null);

      // Liste der erfolgreich deaktivierten Feiertag-IDs für finale Prüfung
      let deactivatedIds: string[] = [];

      // Final validation
      if (!formData.name.trim() || !formData.startDate || !formData.endDate) {
        setError("Name, Startdatum und Enddatum sind erforderlich");
        return;
      }

      if (formData.startDate > formData.endDate) {
        setError("Startdatum darf nicht nach dem Enddatum liegen");
        return;
      }

      let newHoliday: Holiday;

      // Use 3CX integration version if connected, otherwise use regular version
      if (threeCXStatus?.connected) {
        console.log("📞 Feiertag mit 3CX Integration hinzufügen...");
        newHoliday = await apiClient.addHolidayWith3CX(formData);
        console.log("✅ Feiertag erfolgreich mit 3CX synchronisiert");
      } else {
        console.log("📝 Feiertag ohne 3CX Integration hinzufügen...");
        newHoliday = await apiClient.addHoliday(formData);
        console.log("✅ Feiertag erfolgreich hinzugefügt");
      }

      // Optimistic Update: Neuen Feiertag sofort zur Liste hinzufügen
      setHolidays((prev) => [...prev, newHoliday]);

      // Reset form and close dialog immediately
      resetForm();

      // Silent reload für Konsistenz im Hintergrund
      setTimeout(() => loadHolidays(true), 1000);
    } catch (err: any) {
      console.error("❌ Fehler beim Hinzufügen des Feiertags:", err);
      setError(err.message || "Fehler beim Hinzufügen des Feiertags");
    } finally {
      setSaving(false);
    }
  };

  // Hilfsfunktion zum Zurücksetzen des Formulars
  const resetForm = () => {
    setFormData({
      name: "Betriebsferien",
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 Tage später
      type: HolidayType.VACATION,
      description: "",
    });
    setValidationErrors([]);
    setConflictingHolidays([]);
    setShowConflictResolver(false);
    setConflictResolution({});
    setAddDialogOpen(false);
  };

  // Handler für Konfliktauflösung
  const handleConflictResolutionChange = (
    holidayId: string,
    shouldDeactivate: boolean
  ) => {
    setConflictResolution((prev) => ({
      ...prev,
      [holidayId]: shouldDeactivate,
    }));
  };

  // Prüft ob ein Feiertag aufgrund von Konflikten nicht aktiviert werden kann
  const canActivateHoliday = (holiday: Holiday): boolean => {
    const conflicts = checkDateOverlap(
      new Date(holiday.startDate),
      new Date(holiday.endDate),
      holiday.id
    );
    return conflicts.length === 0;
  };

  // Hilfsfunktion: Prüft und aktiviert deaktivierte Feiertage nach dem Löschen/Deaktivieren eines anderen
  const checkAndActivateDeactivatedHolidays = async (
    changedHoliday: Holiday,
    holidaysArray?: Holiday[]
  ) => {
    const currentHolidays = holidaysArray || holidays;

    console.log(
      "🔍 Prüfe alle deaktivierte Feiertage für mögliche Reaktivierung...",
      {
        changedHoliday: changedHoliday.name,
        changedStart: changedHoliday.startDate,
        changedEnd: changedHoliday.endDate,
        usingProvidedArray: !!holidaysArray,
      }
    );

    // Finde alle deaktivierten Feiertage
    const deactivatedHolidays = currentHolidays.filter(
      (holiday) => !holiday.isActive
    );

    console.log(
      `📋 Gefundene deaktivierte Feiertage: ${deactivatedHolidays.length}`,
      deactivatedHolidays.map((h) => h.name)
    );

    // Für jeden deaktivierten Feiertag prüfen, ob er jetzt aktiviert werden kann
    for (const deactivatedHoliday of deactivatedHolidays) {
      // Manuelle Überlappungsprüfung mit dem bereitgestellten Array
      const conflicts = currentHolidays.filter((holiday) => {
        // Überspringen wenn es das gleiche Holiday ist
        if (holiday.id === deactivatedHoliday.id) return false;

        // Nur aktive Feiertage prüfen
        if (!holiday.isActive) return false;

        const holidayStart = new Date(holiday.startDate);
        const holidayEnd = new Date(holiday.endDate);
        const deactivatedStart = new Date(deactivatedHoliday.startDate);
        const deactivatedEnd = new Date(deactivatedHoliday.endDate);

        // Normalisierte Datumsvergleiche (ohne Zeit)
        const hStart = new Date(
          holidayStart.getFullYear(),
          holidayStart.getMonth(),
          holidayStart.getDate()
        );
        const hEnd = new Date(
          holidayEnd.getFullYear(),
          holidayEnd.getMonth(),
          holidayEnd.getDate()
        );
        const dStart = new Date(
          deactivatedStart.getFullYear(),
          deactivatedStart.getMonth(),
          deactivatedStart.getDate()
        );
        const dEnd = new Date(
          deactivatedEnd.getFullYear(),
          deactivatedEnd.getMonth(),
          deactivatedEnd.getDate()
        );

        // Überlappung prüfen: (dStart <= hEnd) && (dEnd >= hStart)
        return dStart <= hEnd && dEnd >= hStart;
      });

      if (conflicts.length === 0) {
        console.log(
          `✅ Keine Konflikte für "${deactivatedHoliday.name}" - aktiviere automatisch...`
        );

        try {
          // Optimistic Update
          updateHolidayInState(deactivatedHoliday.id, { isActive: true });

          // API Call
          const result = await apiClient.activateHoliday(deactivatedHoliday.id);

          if (
            result &&
            typeof result === "object" &&
            result.success === false
          ) {
            // Fehler: Optimistic Update rückgängig machen
            updateHolidayInState(deactivatedHoliday.id, { isActive: false });
            console.error(
              `❌ Fehler beim automatischen Aktivieren von "${deactivatedHoliday.name}":`,
              result.error
            );
          } else {
            console.log(
              `🎉 "${deactivatedHoliday.name}" wurde automatisch reaktiviert!`
            );
          }
        } catch (err: any) {
          // Fehler: Optimistic Update rückgängig machen
          updateHolidayInState(deactivatedHoliday.id, { isActive: false });
          console.error(
            `❌ Fehler beim automatischen Aktivieren von "${deactivatedHoliday.name}":`,
            err.message
          );
        }
      } else {
        console.log(
          `⚠️ "${deactivatedHoliday.name}" hat noch Konflikte:`,
          conflicts.map((c) => c.name)
        );
      }
    }
  };

  const handleDeleteHoliday = async (
    holidayId: string,
    holidayName: string,
    isAutoImported: boolean
  ) => {
    const actionText = isAutoImported ? "deaktivieren" : "löschen";
    if (
      !window.confirm(
        `Möchten Sie den Feiertag "${holidayName}" wirklich ${actionText}?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      console.log(`🗑️ Feiertag ${actionText}...`, {
        holidayId,
        holidayName,
        isAutoImported,
      });

      // Den zu löschenden Feiertag finden, um seinen Zeitraum zu speichern
      const holidayToDelete = holidays.find((h) => h.id === holidayId);

      // Optimistic Update: Feiertag aus der Liste entfernen oder deaktivieren
      if (isAutoImported) {
        updateHolidayInState(holidayId, { isActive: false });
      } else {
        // Berechne den neuen State manuell für die Konfliktprüfung
        const newHolidays = holidays.filter((h) => h.id !== holidayId);
        setHolidays(newHolidays);
        console.log(
          "🗑️ Holiday removed from state, remaining:",
          newHolidays.length
        );
      }

      const result = await apiClient.deleteHoliday(holidayId);

      console.log("📥 Delete API Result:", result);

      if (result && typeof result === "object") {
        if (result.action === "deactivated") {
          console.log("✅ Automatischer Feiertag wurde deaktiviert");
        } else if (result.action === "deleted") {
          console.log("✅ Manueller Feiertag wurde gelöscht");
        } else {
          console.log("✅ Feiertag wurde verarbeitet");
        }
      } else {
        console.log(
          "✅ Feiertag wurde verarbeitet (unbekannte Antwortstruktur)"
        );
      }

      // Nach dem Löschen/Deaktivieren: Prüfe, ob deaktivierte Feiertage wieder aktiviert werden können
      if (holidayToDelete && !isAutoImported) {
        // Verwende den neuen State ohne den gelöschten Feiertag
        const newHolidays = holidays.filter((h) => h.id !== holidayId);
        await checkAndActivateDeactivatedHolidays(holidayToDelete, newHolidays);
      } else if (holidayToDelete && isAutoImported) {
        // Für auto-imported Feiertage verwende den aktuellen State (Feiertag ist nur deaktiviert)
        await checkAndActivateDeactivatedHolidays(holidayToDelete);
      }

      // Silent reload für vollständige Konsistenz
      setTimeout(() => loadHolidays(true), 1000);
    } catch (err: any) {
      console.error(`❌ Fehler beim ${actionText}:`, err);

      // Optimistic Update rückgängig machen
      loadHolidays(true);

      setError(err.message || `Fehler beim ${actionText} des Feiertags`);
    } finally {
      setSaving(false);
    }
  };

  const handleActivateHoliday = async (
    holidayId: string,
    holidayName: string
  ) => {
    try {
      setSaving(true);
      setError(null);

      console.log("🔼 Feiertag aktivieren...", { holidayId, holidayName });

      // Frontend-Validierung: Prüfe auf Konflikte BEVOR die API aufgerufen wird
      const holidayToActivate = holidays.find((h) => h.id === holidayId);
      if (holidayToActivate) {
        const conflicts = checkDateOverlap(
          new Date(holidayToActivate.startDate),
          new Date(holidayToActivate.endDate),
          holidayId
        );

        if (conflicts.length > 0) {
          const conflictNames = conflicts.map((c) => c.name).join(", ");
          setError(
            `Aktivierung nicht möglich: Überschneidung mit aktiven Feiertagen: ${conflictNames}. ` +
              `Bitte deaktivieren Sie diese zuerst oder wählen Sie einen anderen Zeitraum.`
          );
          return;
        }
      }

      // Optimistic Update: Sofort in der UI anzeigen
      updateHolidayInState(holidayId, { isActive: true });

      const result = await apiClient.activateHoliday(holidayId);

      console.log("📥 Activate API Result:", result);

      // Robuste Prüfung der API-Antwort
      if (result && typeof result === "object") {
        if (result.success === false) {
          // Optimistic Update rückgängig machen
          updateHolidayInState(holidayId, { isActive: false });

          if (result.conflicts && result.conflicts.length > 0) {
            const conflictNames = result.conflicts
              .map((c) => c.name)
              .join(", ");
            setError(
              `Aktivierung nicht möglich: Überschneidung mit: ${conflictNames}`
            );
          } else {
            setError(result.error || "Fehler beim Aktivieren des Feiertags");
          }
          return;
        }

        console.log("✅ Feiertag erfolgreich aktiviert");

        // Update mit korrekten Daten vom Server (falls vorhanden)
        if (result.holiday) {
          updateHolidayInState(holidayId, result.holiday);
        }
      } else {
        console.log(
          "✅ Feiertag erfolgreich aktiviert (unbekannte Antwortstruktur)"
        );
      }

      // Silent reload für vollständige Konsistenz
      setTimeout(() => loadHolidays(true), 1000);
    } catch (err: any) {
      console.error("❌ Fehler beim Aktivieren:", err);

      // Optimistic Update rückgängig machen
      updateHolidayInState(holidayId, { isActive: false });

      setError(err.message || "Fehler beim Aktivieren des Feiertags");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateHoliday = async (
    holidayId: string,
    holidayName: string
  ) => {
    if (
      !window.confirm(
        `Möchten Sie den Feiertag "${holidayName}" wirklich deaktivieren?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      console.log("🔽 Feiertag deaktivieren...", { holidayId, holidayName });

      // Den zu deaktivierenden Feiertag finden, um seinen Zeitraum zu speichern
      const holidayToDeactivate = holidays.find((h) => h.id === holidayId);

      // Optimistic Update: Sofort in der UI anzeigen
      updateHolidayInState(holidayId, { isActive: false });

      const result = await apiClient.deactivateHoliday(holidayId);

      console.log("📥 Deactivate API Result:", result);

      // Robuste Prüfung der API-Antwort
      if (result && typeof result === "object") {
        if (result.success === false) {
          // Optimistic Update rückgängig machen
          updateHolidayInState(holidayId, { isActive: true });

          setError(result.error || "Fehler beim Deaktivieren des Feiertags");
          return;
        }

        console.log("✅ Feiertag erfolgreich deaktiviert");

        // Update mit korrekten Daten vom Server (falls vorhanden)
        if (result.holiday) {
          updateHolidayInState(holidayId, result.holiday);
        }
      } else {
        console.log(
          "✅ Feiertag erfolgreich deaktiviert (unbekannte Antwortstruktur)"
        );
      }

      // Nach der Deaktivierung: Prüfe, ob deaktivierte Feiertage wieder aktiviert werden können
      if (holidayToDeactivate) {
        // Verwende den neuen State mit dem deaktivierten Feiertag
        const newHolidays = holidays.map((h) =>
          h.id === holidayId ? { ...h, isActive: false } : h
        );
        await checkAndActivateDeactivatedHolidays(
          holidayToDeactivate,
          newHolidays
        );
      }

      // Silent reload für vollständige Konsistenz
      setTimeout(() => loadHolidays(true), 1000);
    } catch (err: any) {
      console.error("❌ Fehler beim Deaktivieren:", err);

      // Optimistic Update rückgängig machen
      updateHolidayInState(holidayId, { isActive: true });

      setError(err.message || "Fehler beim Deaktivieren des Feiertags");
    } finally {
      setSaving(false);
    }
  };

  const getHolidayTypeLabel = (type: HolidayType): string => {
    const labels: Record<HolidayType, string> = {
      [HolidayType.HOLIDAY]: "Feiertag",
      [HolidayType.VACATION]: "Urlaub",
      [HolidayType.MAINTENANCE]: "Wartung",
      [HolidayType.OTHER]: "Sonstiges",
    };
    return labels[type];
  };

  const isAutoImported = (holiday: Holiday): boolean => {
    return holiday.description?.includes("Automatisch importiert") || false;
  };

  const getHolidayIcon = (holiday: Holiday) => {
    if (isAutoImported(holiday)) {
      return <PublicIcon sx={{ fontSize: 16, mr: 1 }} />;
    }
    return <EventIcon sx={{ fontSize: 16, mr: 1 }} />;
  };

  const getHolidayStatusColor = (holiday: Holiday) => {
    if (!holiday.isActive) {
      return "rgba(0, 0, 0, 0.4)"; // Grau für deaktivierte
    }
    return isAutoImported(holiday) ? "#1976d2" : "#2e7d32"; // Blau für automatisch, Grün für manuell
  };

  const getHolidayTypeColor = (
    type: HolidayType
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    const colors: Record<
      HolidayType,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      [HolidayType.HOLIDAY]: "primary",
      [HolidayType.VACATION]: "success",
      [HolidayType.MAINTENANCE]: "warning",
      [HolidayType.OTHER]: "default",
    };
    return colors[type];
  };

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("de-DE", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateRange = (
    startDate: Date | string,
    endDate: Date | string
  ): string => {
    const startDateObj =
      typeof startDate === "string" ? new Date(startDate) : startDate;
    const endDateObj =
      typeof endDate === "string" ? new Date(endDate) : endDate;

    if (startDateObj.toDateString() === endDateObj.toDateString()) {
      return formatDate(startDateObj);
    }
    return `${formatDate(startDateObj)} - ${formatDate(endDateObj)}`;
  };



  if (loading) {
    return <LoadingSpinner text="Lade Feiertage..." />;
  }

  return (
    <>
      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <EventIcon />
              Feiertage & Ferien
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                size="small"
              >
                Hinzufügen
              </Button>
            </Box>
          </Box>



          <Divider sx={{ mb: 3 }} />

          {/* 3CX Integration */}
          <ThreeCXIntegration
            title="3CX Feiertage Integration"
            type="holidays"
            onSyncSuccess={() => {
              setError(null);
              loadHolidays(); // Reload holidays after sync
            }}
            onSyncError={(error) => {
              setError(`3CX Synchronisation fehlgeschlagen: ${error}`);
            }}
          />

          <Divider sx={{ mb: 3 }} />

          {/* German Holidays Section */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Deutsche Feiertage
                </Typography>
                <Chip
                  icon={<PublicIcon />}
                  label="RLP"
                  color="info"
                  size="small"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatische Synchronisation der deutschen Feiertage für
                Rheinland-Pfalz. Feiertage werden für die nächsten Jahre
                importiert und mit 3CX synchronisiert.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={handleForceUpdateGermanHolidays}
                  disabled={saving}
                >
                  {saving ? "Aktualisiere..." : "Force Update"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Divider sx={{ mb: 3 }} />

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Holidays List */}
          {holidays.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <EventIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Keine Feiertage vorhanden
              </Typography>
              <Typography variant="body2">
                Fügen Sie Ihren ersten Feiertag oder Urlaub hinzu
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {holidays.map((holiday) => (
                <Card
                  key={holiday.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    opacity: holiday.isActive ? 1 : 0.6,
                    backgroundColor: holiday.isActive ? "inherit" : "#f5f5f5",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", flex: 1 }}
                      >
                        {getHolidayIcon(holiday)}
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{
                              textDecoration: holiday.isActive
                                ? "none"
                                : "line-through",
                              color: getHolidayStatusColor(holiday),
                            }}
                          >
                            {holiday.name}
                            {!holiday.isActive && (
                              <Chip
                                label="Deaktiviert"
                                size="small"
                                color="default"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              textDecoration: holiday.isActive
                                ? "none"
                                : "line-through",
                            }}
                          >
                            {formatDateRange(
                              holiday.startDate,
                              holiday.endDate
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Chip
                          label={getHolidayTypeLabel(holiday.type)}
                          color={getHolidayTypeColor(holiday.type)}
                          size="small"
                        />
                        {isAutoImported(holiday) && (
                          <Chip
                            label="Automatisch"
                            color="info"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>

                    {holiday.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontStyle: holiday.isActive ? "normal" : "italic",
                          color: holiday.isActive ? "inherit" : "text.disabled",
                        }}
                      >
                        {holiday.description}
                      </Typography>
                    )}
                  </CardContent>
                  <Divider />
                  <CardContent
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    {isAutoImported(holiday) ? (
                      // Automatisch importierte Feiertage - nur aktivieren/deaktivieren
                      <>
                        {holiday.isActive ? (
                          <Button
                            size="small"
                            color="warning"
                            startIcon={
                              saving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <WarningIcon />
                              )
                            }
                            onClick={() =>
                              handleDeactivateHoliday(holiday.id, holiday.name)
                            }
                            disabled={saving}
                          >
                            {saving ? "Deaktiviere..." : "Deaktivieren"}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="success"
                            startIcon={
                              saving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <RefreshIcon />
                              )
                            }
                            onClick={() =>
                              handleActivateHoliday(holiday.id, holiday.name)
                            }
                            disabled={saving || !canActivateHoliday(holiday)}
                            title={
                              !canActivateHoliday(holiday)
                                ? "Aktivierung nicht möglich - Konflikte mit anderen aktiven Feiertagen"
                                : ""
                            }
                          >
                            {saving ? "Aktiviere..." : "Aktivieren"}
                          </Button>
                        )}
                      </>
                    ) : (
                      // Manuell erstellte Feiertage - nur löschen oder aktivieren
                      <>
                        {!holiday.isActive && (
                          <Button
                            size="small"
                            color="success"
                            startIcon={
                              saving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <RefreshIcon />
                              )
                            }
                            onClick={() =>
                              handleActivateHoliday(holiday.id, holiday.name)
                            }
                            disabled={saving || !canActivateHoliday(holiday)}
                            title={
                              !canActivateHoliday(holiday)
                                ? "Aktivierung nicht möglich - Konflikte mit anderen aktiven Feiertagen"
                                : ""
                            }
                          >
                            {saving ? "Aktiviere..." : "Aktivieren"}
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={
                            saving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DeleteIcon />
                            )
                          }
                          onClick={() =>
                            handleDeleteHoliday(
                              holiday.id,
                              holiday.name,
                              isAutoImported(holiday)
                            )
                          }
                          disabled={saving}
                        >
                          {saving ? "Lösche..." : "Löschen"}
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Add Holiday Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !saving && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon />
          Feiertag hinzufügen
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              placeholder="z.B. Weihnachten, Betriebsferien"
              error={validationErrors.some((e) => e.includes("Name"))}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Startdatum"
                  type="date"
                  value={formData.startDate.toISOString().split("T")[0]}
                  onChange={(e) =>
                    handleInputChange("startDate", new Date(e.target.value))
                  }
                  required
                  InputLabelProps={{ shrink: true }}
                  error={validationErrors.some((e) => e.includes("Startdatum"))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Enddatum"
                  type="date"
                  value={formData.endDate.toISOString().split("T")[0]}
                  onChange={(e) =>
                    handleInputChange("endDate", new Date(e.target.value))
                  }
                  required
                  InputLabelProps={{ shrink: true }}
                  error={validationErrors.some(
                    (e) => e.includes("Enddatum") || e.includes("Startdatum")
                  )}
                />
              </Grid>
            </Grid>

            {/* Konfliktresolver */}
            {showConflictResolver && conflictingHolidays.length > 0 && (
              <Card
                variant="outlined"
                sx={{ borderRadius: 2, bgcolor: "#fff3cd" }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <WarningIcon color="warning" />
                    Überlappende Feiertage gefunden
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Die folgenden Feiertage überlappen mit Ihrem gewählten
                    Zeitraum. Alle überlappenden Feiertage werden automatisch
                    deaktiviert, wenn Sie fortfahren:
                  </Typography>

                  <Stack spacing={1}>
                    {conflictingHolidays.map((conflict) => (
                      <Box
                        key={conflict.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1,
                          bgcolor: "white",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <input
                          type="checkbox"
                          id={`conflict-${conflict.id}`}
                          checked={conflictResolution[conflict.id] || false}
                          onChange={(e) =>
                            handleConflictResolutionChange(
                              conflict.id,
                              e.target.checked
                            )
                          }
                          style={{ transform: "scale(1.2)" }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {conflict.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateRange(
                              conflict.startDate,
                              conflict.endDate
                            )}
                            {isAutoImported(conflict) &&
                              " • Automatisch importiert"}
                          </Typography>
                        </Box>
                        <Chip
                          label={getHolidayTypeLabel(conflict.type)}
                          color={getHolidayTypeColor(conflict.type)}
                          size="small"
                        />
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <FormControl fullWidth>
              <InputLabel>Typ</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange("type", e.target.value)}
                label="Typ"
              >
                {Object.values(HolidayType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getHolidayTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Beschreibung"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              multiline
              rows={2}
              placeholder="Optionale Beschreibung"
            />

            {/* Validierungsstatus-Indikator */}
            {validating && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Prüfe Überlappungen...
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => resetForm()} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleAddHoliday}
            disabled={
              saving ||
              validationErrors.filter((e) => !e.includes("Überlappung"))
                .length > 0 ||
              !formData.name.trim() ||
              (conflictingHolidays.length > 0 &&
                Object.values(conflictResolution).every((v) => !v))
            }
            sx={{
              minWidth: saving ? "140px" : "auto", // Breite während Laden fixieren
            }}
          >
            {saving ? "Wird hinzugefügt..." : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HolidaysSettings;
